// Security Alerts (jobq-api "After-hours security", 2026-10-12).
//
// Who gets the after-hours camera alerts and laser notices by text, and each person's opt-in.
// Texts go only to people who ticked the box here: jobq-api stores the consent text they saw,
// who ticked it and when (PATCH /security/people/{id} {sms_opt_in}). Twilio's 10DLC review of
// the alerts number needs that opt-in to be real and recorded. A new number ends the opt-in.
//
// Since 2026-10-13 every /security call carries the admin session's PIN (securityFetch,
// X-Admin-Pin): the API key ships in this bundle, so jobq-api refuses it alone here. The API
// records the Admin whose PIN made the opt-in; opted_in_by is still sent for an older API.

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { currentActor } from '../utils/beacons';
import { securityFetch as jsonFetch, needsAdminPin } from '../utils/securityApi';
import { AdminContext, requestAdminPin } from '../adminSession.jsx';

export function phoneText(e164) {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164 || '');
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : (e164 || '');
}

export function stampText(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function optInStatus(p) {
  if (p.sms_opt_in_at) {
    return `Opted in ${stampText(p.sms_opt_in_at)}${p.sms_opt_in_by ? ` by ${p.sms_opt_in_by}` : ''}`;
  }
  if (p.sms_opt_out_at) return `Opted out ${stampText(p.sms_opt_out_at)}`;
  return 'Not opted in: gets no texts';
}

function PersonCard({ person, terms, onSaved }) {
  const [phone, setPhone] = useState(phoneText(person.phone_e164));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const optedIn = !!person.sms_opt_in_at;
  const phoneChanged = phone.replace(/\D/g, '') !== (person.phone_e164 || '').replace(/^\+1/, '').replace(/\D/g, '');

  useEffect(() => { setPhone(phoneText(person.phone_e164)); }, [person.phone_e164]);

  const patch = async (body, done) => {
    setBusy(true);
    setMsg(null);
    const r = await jsonFetch(`/security/people/${person.person_id}`, { method: 'PATCH', body: JSON.stringify(body) });
    setBusy(false);
    if (!r.ok) {
      setMsg({ kind: 'danger', text: r.message });
      return;
    }
    const conf = r.body && r.body.confirmation_text;
    if (conf && !conf.sent) {
      setMsg({ kind: 'warning', text: `${done} The confirmation text did not go out: ${conf.info}` });
    } else {
      setMsg({ kind: 'success', text: conf ? `${done} A confirmation text is on its way from (620) 522-1165.` : done });
    }
    onSaved(r.body);
  };

  const toggle = (e) => {
    const on = e.target.checked;
    patch(on ? { sms_opt_in: true, opted_in_by: currentActor() } : { sms_opt_in: false },
      on ? `${person.name} will get alert texts at ${phoneText(person.phone_e164)}.` : `${person.name} will get no more alert texts.`);
  };

  const savePhone = (e) => {
    e.preventDefault();
    patch({ phone }, optedIn ? 'Number saved. The opt-in was for the old number: tick the box again to get texts here.' : 'Number saved.');
  };

  return (
    <div className={`card mb-3${person.is_active ? '' : ' opacity-50'}`} data-testid={`person-${person.person_id}`}>
      <div className="card-body">
        <div className="d-flex flex-wrap align-items-baseline gap-2 mb-2">
          <h5 className="mb-0">{person.name}</h5>
          {!person.is_active && <span className="badge bg-secondary">Switched off</span>}
          <span className={`badge ${optedIn ? 'bg-success' : 'bg-light text-dark border'}`}>{optInStatus(person)}</span>
        </div>

        <form className="row g-2 align-items-end mb-3" onSubmit={savePhone}>
          <div className="col-sm-5 col-md-4">
            <label className="form-label small mb-1" htmlFor={`phone-${person.person_id}`}>Mobile number</label>
            <input
              id={`phone-${person.person_id}`}
              className="form-control"
              type="tel"
              value={phone}
              placeholder="(620) 555-0123"
              onChange={(e) => setPhone(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="col-auto">
            <button type="submit" className="btn btn-outline-primary" disabled={busy || !phoneChanged}>Save number</button>
          </div>
        </form>

        <div className={`border rounded p-3 ${optedIn ? 'border-success' : ''}`}>
          <div className="form-check mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id={`optin-${person.person_id}`}
              checked={optedIn}
              onChange={toggle}
              disabled={busy || !person.phone_e164 || !person.is_active || !terms}
            />
            <label className="form-check-label" htmlFor={`optin-${person.person_id}`}>
              {terms ? terms.text : 'Loading…'}
            </label>
          </div>
          {terms && (
            <div className="small text-muted mt-2 ms-4">
              <a href={terms.terms_url} target="_blank" rel="noreferrer">SMS terms</a>
              {' · '}
              <a href={terms.privacy_url} target="_blank" rel="noreferrer">Privacy policy</a>
              {!person.phone_e164 && <span> · Add a mobile number first.</span>}
            </div>
          )}
        </div>

        {msg && <div className={`alert alert-${msg.kind} py-2 mt-3 mb-0`}>{msg.text}</div>}
      </div>
    </div>
  );
}

export default function SecurityAlertsPage() {
  const admin = useContext(AdminContext);
  const [people, setPeople] = useState(null);
  const [terms, setTerms] = useState(null);
  const [error, setError] = useState(null);
  const [pinNeeded, setPinNeeded] = useState(false);

  // One PIN-checked call first: a stale PIN costs one miss on the API's per-IP limiter, not two.
  const load = useCallback(async () => {
    const p = await jsonFetch('/security/people');
    setPinNeeded(needsAdminPin(p));
    if (!p.ok) {
      setError(p.message);
      return;
    }
    setError(null);
    setPeople(Array.isArray(p.body) ? p.body : []);
    const t = await jsonFetch('/security/sms_opt_in');
    if (t.ok && t.body && t.body.text) setTerms(t.body);
  }, []);
  useEffect(() => { load(); }, [load]);

  // The API refused the session's PIN (none kept, wrong, or not an Admin's): ask for it again.
  const unlock = async () => {
    const user = await requestAdminPin();
    if (!user) return;
    if (admin && admin.loginAsAdmin) {
      admin.loginAsAdmin({ userId: user.userId, userName: user.userName, role: user.role, pin: user.pin },
        { remember: user.remember });
    } else {
      sessionStorage.setItem('userRole', String(user.role || '').toLowerCase());
      sessionStorage.setItem('adminPin', user.pin);
    }
    load();
  };

  const saved = (row) => setPeople((list) => (list || []).map((x) => (x.person_id === row.person_id ? { ...x, ...row } : x)));

  return (
    <div className="container py-4" style={{ maxWidth: 820 }}>
      <h2 className="mb-1">Security Alerts</h2>
      <p className="text-muted">
        After-hours security camera alerts and laser notices are texted only to the people below who
        opt in. Each person ticks their own box; untick it (or reply STOP to any alert) to stop the texts.
      </p>
      {error && (
        <div className="alert alert-danger d-flex flex-wrap align-items-center gap-2">
          <span>{pinNeeded ? `These settings need an Admin PIN. ${error}` : error}</span>
          {pinNeeded && (
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={unlock}>Enter Admin PIN</button>
          )}
        </div>
      )}
      {!people && !error && <div className="text-muted">Loading…</div>}
      {people && people.length === 0 && <div className="text-muted">Nobody is set up for alerts yet.</div>}
      {people && people.map((p) => <PersonCard key={p.person_id} person={p} terms={terms} onSaved={saved} />)}
    </div>
  );
}
